import {
  pageToPath,
  scanDetailPath,
  riskDetailPath,
} from '../../routes/dashboardRoutes';

/**
 * Map API / legacy notification destinations onto SPA `/app/...` paths.
 * Many backend producers omit `link` or store Django/admin URLs — resolve a usable route.
 */
export function resolveNotificationTargetPath(notification) {
  const raw = notification && typeof notification === 'object' ? notification : {};
  const explicit = String(raw.link || raw.url || raw.href || '').trim();
  const fromExplicit = normalizeToAppPath(explicit);
  if (fromExplicit) return fromExplicit;

  const eventType = String(
    raw.sourceEventType || raw.type || raw.verb || raw.source_type || ''
  )
    .trim()
    .toUpperCase();
  const sourceType = String(raw.sourceType || raw.source_type || '').trim().toLowerCase();
  const objectId = String(raw.sourceObjectId || raw.source_object_id || raw.objectId || '').trim();
  const text = `${raw.title || ''} ${raw.message || ''}`.toLowerCase();

  if (
    eventType.includes('SCANNER') ||
    sourceType === 'scanner' ||
    /\bscanner\b/.test(text)
  ) {
    return pageToPath('settingsDeployment');
  }

  if (
    eventType.includes('VULNERABILITY') ||
    sourceType === 'vulnerability' ||
    (/\bvulnerabilit/.test(text) && !/no vulnerabilit/.test(text))
  ) {
    if (objectId) return riskDetailPath(objectId);
    return pageToPath('risks');
  }

  if (
    eventType.includes('SUPPORT') ||
    sourceType === 'support' ||
    /\bticket\b|\bsupport\b/.test(text)
  ) {
    return pageToPath('support');
  }

  if (
    eventType.includes('EARLY') ||
    eventType.includes('GHOST') ||
    eventType.includes('DISCLOSURE') ||
    sourceType === 'early_intel' ||
    /early disclosure|ghost cve/.test(text)
  ) {
    return pageToPath('earlyDisclosures');
  }

  if (
    eventType.includes('SUBSCRIPTION') ||
    eventType.includes('BILLING') ||
    /\bsubscription\b|\bbilling\b|\bplan\b/.test(text)
  ) {
    return pageToPath('subscription');
  }

  if (
    eventType.includes('SCAN') ||
    sourceType === 'scan' ||
    /\bscan\b/.test(text)
  ) {
    if (objectId) {
      const scanType = String(raw.scanType || raw.scan_type || raw.metadata?.scan_type || '')
        .trim()
        .toLowerCase();
      const inferred =
        scanType === 'network' || /network/.test(text)
          ? 'network'
          : scanType === 'web' || /web|zap|url/.test(text)
            ? 'web'
            : 'web';
      return scanDetailPath(objectId, inferred);
    }
    if (/fail|error|interrupted/.test(text)) {
      return `${pageToPath('scans')}?status=failed`;
    }
    if (/schedul|progress|recover|started|running/.test(text)) {
      return `${pageToPath('scans')}?status=in_progress`;
    }
    return pageToPath('scans');
  }

  return '';
}

function normalizeToAppPath(rawLink) {
  const value = String(rawLink || '').trim();
  if (!value) return '';

  let pathname = value;
  let search = '';
  let hash = '';

  try {
    if (/^https?:\/\//i.test(value)) {
      const url = new URL(value);
      pathname = url.pathname || '/';
      search = url.search || '';
      hash = url.hash || '';
    } else if (value.startsWith('//')) {
      const url = new URL(`https:${value}`);
      pathname = url.pathname || '/';
      search = url.search || '';
      hash = url.hash || '';
    } else {
      const match = value.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/);
      pathname = match?.[1] || value;
      search = match?.[2] || '';
      hash = match?.[3] || '';
    }
  } catch {
    return value.startsWith('/') ? value : '';
  }

  const normalized = String(pathname || '').replace(/\/+$/, '') || '/';

  // Already an SPA route.
  if (normalized === '/app' || normalized.startsWith('/app/')) {
    return `${normalized}${search}${hash}`;
  }

  // Legacy Django dashboard aliases → SPA.
  const legacyMap = [
    [/^\/dashboard\/?$/, pageToPath('dashboard')],
    [/^\/dashboard\/scans(?:\/|$)/, pageToPath('scans')],
    [/^\/dashboard\/risks(?:\/|$)/, pageToPath('risks')],
    [/^\/dashboard\/reports(?:\/|$)/, pageToPath('reports')],
    [/^\/dashboard\/support(?:\/|$)/, pageToPath('support')],
    [/^\/webscanners(?:\/|$)/, pageToPath('scans')],
    [/^\/networkscanners(?:\/|$)/, pageToPath('scans')],
    [/^\/scans(?:\/|$)/, pageToPath('scans')],
    [/^\/risks(?:\/|$)/, pageToPath('risks')],
    [/^\/vulnerabilit(?:y|ies)(?:\/|$)/, pageToPath('risks')],
    [/^\/support(?:\/|$)/, pageToPath('support')],
    [/^\/settings\/deployment(?:\/|$)/, pageToPath('settingsDeployment')],
    [/^\/settings\/connectors(?:\/|$)/, pageToPath('settingsConnectors')],
    [/^\/early-disclosures(?:\/|$)/, pageToPath('earlyDisclosures')],
    [/^\/subscription(?:\/|$)/, pageToPath('subscription')],
  ];

  for (const [pattern, dest] of legacyMap) {
    if (pattern.test(normalized)) {
      return `${dest}${search}${hash}`;
    }
  }

  // Relative path that is not under /app — treat as SPA if it looks like one segment page.
  if (value.startsWith('/') && !normalized.startsWith('/api')) {
    return `${normalized}${search}${hash}`;
  }

  return '';
}
